import { Box } from "@mui/material";
import React from "react";
import { MessageType } from "../../../interfaces/all";
import DateSeparator from "./DateSeparator";
import Message from "./Message";
import { MessageWrapper } from "./styles";
import UnreadSeparator from "./UnreadSeparator";

interface MessageGroupProps {
  groupDate: string;
  messages: MessageType[];
  startIndex: number;
  unreadCount: number;
  firstUnreadIndex: number;
  forceUpdate: number;
  transformMessage: (message: MessageType) => any;
  getMessageReadStatus: (id: string) => any;
}

const MessageGroup: React.FC<MessageGroupProps> = React.memo(
  ({
    groupDate,
    messages,
    startIndex,
    unreadCount,
    firstUnreadIndex,
    forceUpdate,
    transformMessage,
    getMessageReadStatus,
  }) => {
    // Função para filtrar mensagens de reação, mantendo apenas a mais recente para cada message_id
    const filterReactionMessages = React.useCallback(
      (messages: MessageType[]): MessageType[] => {
        const reactionGroups = new Map<string, MessageType>();
        messages.forEach((message) => {
          if (
            message.type === "reaction" &&
            message.content.reaction?.message_id
          ) {
            const messageId = message.content.reaction.message_id;
            const existing = reactionGroups.get(messageId);
            if (
              !existing ||
              new Date(message.timestamp) > new Date(existing.timestamp)
            ) {
              reactionGroups.set(messageId, message);
            }
          }
        });
        return messages
          .map((message) => {
            if (
              message.type === "reaction" &&
              message.content.reaction?.message_id
            ) {
              const latestReaction = reactionGroups.get(
                message.content.reaction.message_id,
              );
              return latestReaction || message;
            }
            return message;
          })
          .filter((message, index, array) => {
            if (
              message.type === "reaction" &&
              message.content.reaction?.message_id
            ) {
              const firstIndex = array.findIndex(
                (m) =>
                  m.type === "reaction" &&
                  m.content.reaction?.message_id ===
                    message.content.reaction?.message_id,
              );
              return index === firstIndex;
            }
            return true;
          });
      },
      [],
    );

    const filteredMessages = React.useMemo(
      () => filterReactionMessages(messages),
      [filterReactionMessages, messages],
    );

    return (
      <Box key={groupDate}>
        <DateSeparator date={groupDate} />
        {filteredMessages.map((message, index) => {
          const transformedMessage = transformMessage(message);
          const isFirstInGroup = index === 0;
          const isFromSameSender =
            index > 0 && filteredMessages[index - 1].sentBy === message.sentBy;
          const showAvatar = !isFromSameSender;
          const currentGlobalIndex = startIndex + index;
          const updatedReadStatus = message._id
            ? getMessageReadStatus(message._id)
            : null;
          const currentReadStatus = updatedReadStatus ||
            message.readStatus || { isRead: false };
          const messageWithUpdatedStatus = {
            ...transformedMessage,
            readStatus: currentReadStatus,
          };

          // Criar uma chave que inclui os statuses para forçar re-render quando mudarem
          const statusKey = messageWithUpdatedStatus.statuses?.length
            ? messageWithUpdatedStatus.statuses
                .map((s: any) => `${s.status}-${s.timestamp}`)
                .join("|")
            : "no-status";

          return (
            <React.Fragment
              key={`${transformedMessage._id}-${forceUpdate}-${currentReadStatus?.isRead ? "read" : "unread"}-${statusKey}`}
            >
              {unreadCount > 0 && currentGlobalIndex === firstUnreadIndex && (
                <UnreadSeparator count={unreadCount} />
              )}
              <MessageWrapper
                isFromSameSender={isFromSameSender}
                isFirstInGroup={isFirstInGroup}
              >
                <Message
                  message={messageWithUpdatedStatus}
                  showAvatar={showAvatar}
                />
              </MessageWrapper>
            </React.Fragment>
          );
        })}
      </Box>
    );
  },
);

MessageGroup.displayName = "MessageGroup";

export default MessageGroup;
